/* In-memory mocked client for Recipe V2 — no real backend exists for this domain yet. */

import { IRecipeV2Client } from './IRecipeV2Client';
import {
  mockRecipesV2,
  mockRecipeV2Details,
  mockAllergens,
  mockProductionMethods,
  mockStockItems,
  mockSupplierComparisons,
} from '../../../../mocks/data/recipeV2Data';
import {
  RecipeV2,
  RecipeV2Detail,
  RecipeV2ListResponse,
  RecipeComponent,
  CreateRecipeRequest,
  ApproveRejectRequest,
  AllergenOverride,
  CostSimulationResult,
  ComponentSupplierComparison,
  StockItemSearchResult,
  ProductionMethod,
  Allergen,
  RecipeStep,
  CreateUpdateStepRequest,
  UpdateComponentRequest,
  UpdateRecipeMetadataRequest,
  DietaryAttribute,
  DietaryAttributeOverride,
  RecipeBulkImportResult,
  RecipeStatus,
  RecipeKind,
  ConvertToStockRequest,
  ConvertToStockResult,
} from './recipeV2Types';

const buildFallbackRecipeDetail = (id: number): RecipeV2Detail | null => {
  const recipe = mockRecipesV2.find((r) => r.id === id);
  if (!recipe) return null;

  const productionMethod =
    recipe.productionMethodId === null
      ? null
      : (mockProductionMethods.find((m) => m.id === recipe.productionMethodId) ?? null);

  return {
    ...recipe,
    components: [],
    allergens: recipe.allergenSummary,
    versions: [],
    aiFindings: [],
    carbonTotal: recipe.totalCost,
    carbonPerPortion: recipe.costPerPortion,
    productionMethod,
    steps: [],
  };
};

export class RecipeV2ClientMocked implements IRecipeV2Client {
  async getRecipes(search: string, page: number, pageSize: number, _storeNumber?: string): Promise<RecipeV2ListResponse> {
    // Demo data ignores store/franchise scoping entirely - return recipes regardless of store.
    let filtered: RecipeV2[] = mockRecipesV2;

    const term = search?.toLowerCase();
    if (term) {
      filtered = filtered.filter(
        (r) =>
          r.description.toLowerCase().includes(term) ||
          r.itemNumber.toLowerCase().includes(term) ||
          r.primaryCategory.toLowerCase().includes(term),
      );
    }

    const start = (page - 1) * pageSize;
    const data = filtered.slice(start, start + pageSize);

    return { data, totalCount: filtered.length, page, pageSize };
  }

  async getRecipeDetail(id: number): Promise<RecipeV2Detail> {
    const detail = mockRecipeV2Details[id] ?? buildFallbackRecipeDetail(id);
    if (!detail) throw new Error(`Recipe ${id} not found`);
    return detail;
  }

  async createRecipe(data: CreateRecipeRequest): Promise<RecipeV2> {
    return {
      id: 99,
      franchiseId: 1,
      itemNumber: data.itemNumber,
      description: data.description,
      status: RecipeStatus.Draft,
      recipeType: RecipeKind.Standard,
      primaryCategory: data.primaryCategory,
      yieldQuantity: data.yieldQuantity,
      yieldUom: data.yieldUom,
      effectiveFrom: data.effectiveFrom,
      effectiveTo: data.effectiveTo,
      productionMethodId: data.productionMethodId,
      carbonScore: null,
      totalCost: 0,
      costPerPortion: 0,
      allergenSummary: [],
      dietaryAttributes: [],
      aiIssueCount: 0,
      currentVersion: 1,
      createdBy: 'Demo User',
      createdAt: new Date().toISOString(),
      modifiedBy: 'Demo User',
      modifiedAt: new Date().toISOString(),
    };
  }

  async updateRecipe(id: number, data: UpdateRecipeMetadataRequest): Promise<RecipeV2Detail> {
    const existing = mockRecipeV2Details[id] ?? buildFallbackRecipeDetail(id);
    if (!existing) throw new Error(`Recipe ${id} not found`);
    return {
      ...existing,
      description: data.description ?? existing.description,
      primaryCategory: data.primaryCategory ?? existing.primaryCategory,
      yieldQuantity: data.yieldQuantity ?? existing.yieldQuantity,
      yieldUom: data.yieldUom ?? existing.yieldUom,
      effectiveFrom: data.effectiveFrom ?? existing.effectiveFrom,
      effectiveTo: data.effectiveTo !== undefined ? data.effectiveTo : existing.effectiveTo,
      productionMethodId: data.productionMethodId !== undefined ? data.productionMethodId : existing.productionMethodId,
      modifiedBy: 'Demo User',
      modifiedAt: new Date().toISOString(),
    };
  }

  async deleteRecipe(_id: number): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async cloneRecipe(id: number): Promise<RecipeV2> {
    const source = mockRecipesV2.find((r) => r.id === id);
    if (!source) throw new Error(`Recipe ${id} not found`);
    return { ...source, id: 100, description: `${source.description} (Copy)`, status: RecipeStatus.Draft, currentVersion: 1 };
  }

  async approveRecipe(_id: number, _data: ApproveRejectRequest): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async rejectRecipe(_id: number, _data: ApproveRejectRequest): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async rollbackVersion(_id: number, _versionNumber: number): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async addComponent(
    recipeId: number,
    component: Omit<UpdateComponentRequest, 'sortOrder'> & { componentProductId: number; componentItemNumber: string; componentDescription: string; sortOrder?: number },
  ): Promise<RecipeComponent> {
    return {
      id: Math.floor(Math.random() * 100000),
      recipeId,
      componentProductId: component.componentProductId,
      componentItemNumber: component.componentItemNumber,
      componentDescription: component.componentDescription,
      quantity: component.quantity,
      weight: component.weight,
      volume: component.volume,
      sortOrder: component.sortOrder ?? 0,
      uom: component.uom,
      costPrice: 0,
      costContribution: 0,
      weightNet: component.weight,
      isRecipe: false,
    };
  }

  async updateComponent(recipeId: number, componentId: number, data: UpdateComponentRequest): Promise<RecipeComponent> {
    return {
      id: componentId,
      recipeId,
      componentProductId: 0,
      componentItemNumber: '',
      componentDescription: '',
      quantity: data.quantity,
      weight: data.weight,
      volume: data.volume,
      sortOrder: data.sortOrder,
      uom: data.uom,
      costPrice: 0,
      costContribution: 0,
      weightNet: data.weight,
      isRecipe: false,
    };
  }

  async removeComponent(_recipeId: number, _componentId: number): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async reorderComponents(_recipeId: number, _componentIds: number[]): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async convertToStock(recipeId: number, request: ConvertToStockRequest): Promise<ConvertToStockResult> {
    // No-op: no real backend/inventory integration - just echoes back what would be held.
    const recipe = mockRecipesV2.find((r) => r.id === recipeId);
    return {
      stockItemNumber: recipe?.itemNumber ?? `BATCH-${recipeId}`,
      quantity: request.quantity,
      uom: request.uom,
    };
  }

  async getSteps(recipeId: number): Promise<RecipeStep[]> {
    const detail = mockRecipeV2Details[recipeId];
    return detail?.steps ?? [];
  }

  async addStep(recipeId: number, step: CreateUpdateStepRequest): Promise<RecipeStep> {
    return {
      id: Math.floor(Math.random() * 100000),
      recipeId,
      stepNumber: step.stepNumber,
      title: step.title,
      instruction: step.instruction,
      productionMethodId: step.productionMethodId,
      durationMinutes: step.durationMinutes,
      temperature: step.temperature,
      equipment: [],
    };
  }

  async updateStep(recipeId: number, stepId: number, step: CreateUpdateStepRequest): Promise<RecipeStep> {
    return {
      id: stepId,
      recipeId,
      stepNumber: step.stepNumber,
      title: step.title,
      instruction: step.instruction,
      productionMethodId: step.productionMethodId,
      durationMinutes: step.durationMinutes,
      temperature: step.temperature,
      equipment: [],
    };
  }

  async removeStep(_recipeId: number, _stepId: number): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async reorderSteps(_recipeId: number, _stepIds: number[]): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async getAllergens(): Promise<Allergen[]> {
    return mockAllergens;
  }

  async overrideAllergen(_recipeId: number, _override: AllergenOverride): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async clearAllergenOverride(_recipeId: number, _allergenId: number): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async getDietaryAttributes(recipeId: number): Promise<DietaryAttribute[]> {
    const detail = mockRecipeV2Details[recipeId];
    return detail?.dietaryAttributes ?? [];
  }

  async overrideDietaryAttribute(_recipeId: number, _override: DietaryAttributeOverride): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async getProductionMethods(): Promise<ProductionMethod[]> {
    return mockProductionMethods;
  }

  async dismissAIFinding(_recipeId: number, _findingId: number, _reason: string): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async acknowledgeAIFinding(_recipeId: number, _findingId: number): Promise<void> {
    // No-op: no real backend, nothing to persist.
  }

  async simulateCost(_id: number, _componentId: number, priceChangePercent: number): Promise<CostSimulationResult> {
    const originalCost = 4.92;
    return {
      originalCost,
      simulatedCost: originalCost * (1 + priceChangePercent / 100),
      difference: originalCost * (priceChangePercent / 100),
      percentageChange: priceChangePercent,
      affectedRecipes: 3,
    };
  }

  async getSupplierComparison(id: number): Promise<ComponentSupplierComparison[]> {
    return mockSupplierComparisons[id] ?? [];
  }

  async searchStockItems(search: string): Promise<StockItemSearchResult[]> {
    const term = search?.toLowerCase() ?? '';
    const stockMatches: StockItemSearchResult[] = mockStockItems
      .filter((i) => i.description.toLowerCase().includes(term) || i.itemNumber.toLowerCase().includes(term));
    // Recipes (sub-recipes) can be added as components too - search includes them alongside stock items.
    const recipeMatches: StockItemSearchResult[] = mockRecipesV2
      .filter((r) => r.description.toLowerCase().includes(term) || r.itemNumber.toLowerCase().includes(term))
      .map((r) => ({
        id: r.id,
        itemNumber: r.itemNumber,
        description: r.description,
        uom: r.yieldUom,
        costPrice: r.costPerPortion,
        weightNet: 0,
        isRecipe: true,
      }));
    return [...recipeMatches, ...stockMatches].slice(0, 10);
  }

  async exportRecipes(_recipeIds: number[], format: 'csv' | 'xlsx'): Promise<Blob> {
    return new Blob(['mock,csv,data'], { type: format === 'csv' ? 'text/csv' : 'application/vnd.ms-excel' });
  }

  async bulkImport(_file: File): Promise<RecipeBulkImportResult> {
    return { imported: 12, skipped: 0, errors: [] };
  }
}
